import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  // desiredPowerOutputUnits,
  // // deviceModelSeries,
  // maxPowerOutputUnits,
  PortalType,
} from '../../../../config/enum';
import { usePortal } from '../../../../contexts/PortalContext/PortalContext';
import { DeviceDetails } from '../../../../interface/device-management-partner.interface';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { setNotificationMessage } from '../../../../redux/slices/NotificationManagementSlice';
import { fetchAllStationsList } from '../../../../redux/slices/Partners/ApprovedLocationManagementSlice';
import { fetchAllDevices } from '../../../../redux/slices/Partners/DeviceManagementSlice';
import MyButton from '../../../../shared-components/button/Button';
import CustomInput from '../../../../shared-components/custom-input/CustomInput';
// import CustomInputSelectBox from '../../../../shared-components/custom-input-select-box/CustomInputSelectBox';
import FormCancelWarning from '../../../../shared-components/form-cancel-warning/FormCancelWarning';
import ModalBox from '../../../../shared-components/modal-box/ModalBox';
import RadioButton from '../../../../shared-components/radio-button/RadioButton';
import SelectFiled from '../../../../shared-components/select-field/SelectField';
import SuccessNotification from '../../../../shared-components/success-notification/SuccessNotification';
import FacilityDetailsSidePanel from '../../../../shared-components/table-side-panel/FacilityDetailsSidePanel';
import TariffNotAppliedWarning from '../../../../shared-components/tariff-not-applied-warning/TariffNotAppliedWarning';
import { TooltipIcon } from '../../../../shared-components/tooltip/TooltipIcon';
import ToFixedValue from '../../../../shared-functions/ToFixedValue';
import { floatingNumberValidation } from '../../../../shared-functions/Validation';
import deviceApi from '../../../../utils/api/partner/device-partner';
import AddFacilityAccount from '../../../account-management/components/add-facility-account';

interface ModalBoxDetail {
  title: string | null;
  content: JSX.Element | null;
  notificationModal: boolean;
}

interface InputInterface {
  label: string;
  value: string;
  description: string;
}

type Option = {
  value: string | number;
  label: string;
};

interface AddDeviceProperties {
  _id: string;
  accountCustomId: string;
  accountName: string;
}

/**
 * AddDevice component handles the creation of new devices in the device management system.
 * It provides a form interface for users to input device details such as name, ID, and power specifications.
 * The component includes form validation, submission handling, and navigation controls.
 * @param {object} param0 - The component props
 * @param {Function} param0.setSidePanelOpen - The setSidePanelOpen prop
 * @param {boolean} [param0.hideHeader] - The hideHeader prop to toggle header display
 * @param {string} [param0.presetAccountCustomId] - When provided (e.g. opened from a partner's
 * details page), the account/partner select field is pre-filled with this account and locked
 * so the user cannot change it.
 * @param {Function} [param0.onSuccess] - Optional callback invoked after device is successfully created
 * @param {Function} [param0.onDeviceAdded] - Optional callback invoked when charger is added with tariff status
 * @returns {any} A form interface for adding new devices
 */
function AddDevice({
  setSidePanelOpen,
  hideHeader = false,
  presetAccountCustomId,
  onSuccess,
  onDeviceAdded,
}: Readonly<{
  setSidePanelOpen: (open: boolean) => void;
  hideHeader?: boolean;
  presetAccountCustomId?: string;
  onSuccess?: () => void;
  onDeviceAdded?: (details: { isTariffApplied: boolean }) => void;
}>) {
  const { t } = useTranslation(['partnerDeviceManagement', 'station', 'sidePanel']);
  const dispatch = useAppDispatch();
  const { partnerStations, loading } = useAppSelector((state) => state.partnersApprovedLocation);
  const { page, limit } = useAppSelector((states) => states.partnersDevice);
  const navigate = useNavigate();
  const [allStationsList, setAllStationsList] = useState<InputInterface[]>([]);
  const [accountNameList, setAccountNameList] = useState<InputInterface[]>([]);
  const [showFacilityPanel, setShowFacilityPanel] = useState(false);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [formStatus, setFormStatus] = useState({
    showFormErrors: false,
    buttonLoader: false,
  });
  const { portalType } = usePortal();

  const [deviceDetails, setDeviceDetails] = useState<DeviceDetails>({
    accountName: '',
    accountCustomId: '',
    chargingStation: '',
    chargingPointSn: '',
    chargingPointName: '',
    maximumOutputPower: 0,
    maximumOutputPowerUnit: 'kW',
    desiredPowerOutput: 0,
    powerUnit: 'W',
    modelSeries: '',
    chargePointModel: '',
    chargePointVendor: '',
    currentType: '',
    skuNumber: '',
    installationType: '',
    networkConnectivity: '',
    plugAndCharge: '',
    connectorInformation: [],
    stationName: '',
    protocolSupport: '',
    stationLocation: '',
    firmwareVersion: '',
    imageUrls: '',
    status: '',
  });

  const [modalBoxStatus, setModalBoxStatus] = useState<'open' | 'close'>('close');
  const [modalBoxDetail, setModalBoxDetail] = useState<ModalBoxDetail>({
    title: '',
    content: null,
    notificationModal: false,
  });

  const [deviceIdValidation, setDeviceIdValidation] = useState({
    error: false,
    message: '',
  });

  /**
   * Fetch the all station data function
   */
  const fetchData = async () => {
    if (!loading) {
      dispatch(fetchAllStationsList());
    }
  };

  /**
   * Updates the device details state when form fields change.
   * @param {string} name - The name of the field to update
   * @param {any} changeValue - The new value for the field
   */
  const handleDeviceDetailChange = (
    name: string,
    changeValue: string | number | boolean | string[],
  ) => {
    setDeviceDetails((previousDetails) => ({
      ...previousDetails,
      [name]: changeValue,
      ...(name === 'accountName' && {
        accountCustomId:
          accountNameList.find((account) => account.value === changeValue)?.value ||
          String(changeValue),
      }),
      ...(name === 'chargingPointSn' && {
        chargePointModel: '',
        chargePointVendor: '',
        currentType: '',
        skuNumber: '',
        installationType: '',
        networkConnectivity: '',
        plugAndCharge: '',
        maximumOutputPower: 0,
        desiredPowerOutput: 0,
        connectorInformation: [],
      }),
    }));
  };

  /**
   * Resets the form state and navigates back to the device management page.
   * This is called after successful submission or when user confirms cancellation.
   */
  const handleNavigateBack = () => {
    setDeviceDetails({
      chargingStation: '',
      chargingPointName: '',
      chargingPointSn: '',
      powerUnit: 'W',
      desiredPowerOutput: 0,
      maximumOutputPower: 0,
      maximumOutputPowerUnit: 'kW',
      modelSeries: '',
      chargePointModel: '',
      currentType: '',
      skuNumber: '',
      installationType: '',
      networkConnectivity: '',
      plugAndCharge: '',
      chargePointVendor: '',
      accountCustomId: '',
      connectorInformation: [],
      firmwareVersion: '',
      status: '',
    });
    setModalBoxStatus('close');
    setSidePanelOpen(false);
    onSuccess?.();
  };

  /**
   * Displays a warning modal when user attempts to cancel form submission.
   * Gives users the option to confirm cancellation or continue editing.
   */
  const handleClickCancel = () => {
    setModalBoxDetail({
      title: '',
      content: (
        <FormCancelWarning
          title={t('cancelTitle')}
          description={t('cancelDescription')}
          cancelButtonLabel={t('cancel')}
          cancelButtonOnClick={() => setModalBoxStatus('close')}
          submitButtonLabel={t('ok')}
          submitButtonOnClick={handleNavigateBack}
        />
      ),
      notificationModal: true,
    });
    setModalBoxStatus('open');
  };

  const [debounceChargingPointSn, setDebounceChargingPointSn] = useState('');

  /**
   * Fetch charge point list
   * @param {any} chargingPointSn - The chargingPointSn
   */
  const handleChargeList = async (chargingPointSn: string) => {
    try {
      setDeviceIdValidation({
        error: false,
        message: '',
      });

      const response = await deviceApi.fetchChargePointList(chargingPointSn);
      const connectorList =
        response?.data?.connectorInformation || response?.data?.connectors || [];

      setDeviceDetails((previousDetails) => ({
        ...previousDetails,
        chargePointModel: response?.data?.modelName || response?.data?.chargePointModel || '',
        chargePointVendor: response?.data?.brandName || response?.data?.chargePointVendor || '',
        currentType: response?.data?.currentType || '',
        skuNumber: response?.data?.skuNumber || '',
        installationType: response?.data?.installationType || '',
        protocolSupport: response?.data?.protocolSupport || '',
        networkConnectivity: response?.data?.networkConnectivity || '',
        plugAndCharge:
          response?.data?.plugAndCharge !== undefined ? String(response?.data?.plugAndCharge) : '',
        firmwareVersion:
          response?.data?.firmwareVersion || response?.data?.specifications?.firmwareVersion || '',
        maximumOutputPower: response?.data?.specifications?.powerOutput
          ? Number(response.data.specifications.powerOutput)
          : Number(response?.data?.maximumOutputPower || 0),
        maximumOutputPowerUnit: response?.data?.maximumOutputPowerUnit || 'kW',
        // desiredPowerOutput: response?.data?.specifications?.powerOutput
        //   ? Number(response.data.specifications.powerOutput)
        //   : Number(response?.data?.desiredPowerOutput || response?.data?.maximumOutputPower || 0),
        powerUnit: 'W',
        connectorInformation: connectorList.map((connector: any) => {
          const capacityValue =
            typeof connector?.connectorRating === 'object'
              ? connector?.connectorRating?.capacityKW
              : connector?.connectorRating;
          return {
            ...connector,
            connectorRating: {
              name: connector?.connectorRating?.name || '',
              currentType: connector?.connectorRating?.currentType || connector?.currentType || '',
              capacityKW: capacityValue || '',
            },
            connectorRatingDetails: {
              capacityKW: Number(capacityValue) || 0,
              unit: connector?.connectorRating?.unit || connector?.unit || 'kW',
            },
          };
        }),
        imageUrls: response?.data?.imageUrls?.[0] || response?.data?.imageUrl || '',
        status: response?.data?.status || '',
      }));

      setDeviceIdValidation({
        error: false,
        message: '',
      });
    } catch (error: any) {
      setDeviceIdValidation({
        error: true,
        message: error?.response?.data?.message || t('deviceIdNotFound'),
      });
      dispatch(
        setNotificationMessage({
          message: error?.response?.data?.message || t('somethingWentWrong'),
          status: true,
          type: 'error',
          code: error?.response?.data?.code,
        }),
      );
    }
  };

  /**
   * Debounce typing
   */
  useEffect(() => {
    setDeviceIdValidation({
      error: false,
      message: '',
    });

    const timer = setTimeout(() => {
      setDebounceChargingPointSn(deviceDetails.chargingPointSn);
    }, 1000);

    return () => clearTimeout(timer);
  }, [deviceDetails.chargingPointSn]);

  /**
   * API call after typing completed
   */
  useEffect(() => {
    if (debounceChargingPointSn?.trim().length > 1) {
      handleChargeList(debounceChargingPointSn);
    }
  }, [debounceChargingPointSn]);

  /**
   * Handles the submission of device details.
   * Validates required fields, creates a FormData object with device details,
   * and makes an API call to add the station. Shows success notification on completion.
   */
  const handleSubmitDeviceDetails = async () => {
    const isAccountRequired = PortalType.NETWORKS === portalType || PortalType.APP === portalType;
    const isAccountValid =
      !isAccountRequired || Boolean(deviceDetails.accountName && deviceDetails.accountCustomId);
    const isStationValid = Boolean(deviceDetails.chargingStation);
    const isDeviceNameValid = Boolean(deviceDetails.chargingPointName?.trim());
    const isDeviceIdEntered = Boolean(deviceDetails.chargingPointSn?.trim());

    if (
      isDeviceIdEntered &&
      !deviceIdValidation.error &&
      (!deviceDetails.connectorInformation || deviceDetails.connectorInformation.length === 0)
    ) {
      setFormStatus((previous) => ({ ...previous, buttonLoader: true }));
      try {
        await handleChargeList(deviceDetails.chargingPointSn);
      } catch {
        // Error is handled inside handleChargeList
      }
    }

    const isDeviceIdValid = isDeviceIdEntered && !deviceIdValidation.error;
    const isDesiredPowerValid =
      (floatingNumberValidation(0.1, Infinity, Number(deviceDetails.desiredPowerOutput)) ||
        Number(deviceDetails.desiredPowerOutput) > 0) &&
      (Number(deviceDetails.maximumOutputPower) === 0 ||
        Number(deviceDetails.desiredPowerOutput) <= Number(deviceDetails.maximumOutputPower));

    if (
      isAccountValid &&
      isStationValid &&
      isDeviceNameValid &&
      isDeviceIdValid &&
      isDesiredPowerValid
    ) {
      setFormStatus({
        showFormErrors: false,
        buttonLoader: true,
      });
      const formattedConnectorInformation = (deviceDetails.connectorInformation || []).map(
        (item, index) => ({
          connectorId: item.connectorId || index + 1,
          connectorType: Array.isArray(item.connectorType)
            ? item.connectorType
            : [item.connectorType].filter(Boolean),
          connectorFormat: item.connectorFormat,
          maxVoltage: item.maxVoltage,
          maxCurrent: item.maxCurrent,
          connectorRating: item.connectorRating,
          unit: item.unit,
          currentType: item.currentType,
          maxPower: item.maxPower,
        }),
      );

      let plugAndChargeValue: boolean | undefined;
      if (deviceDetails.plugAndCharge === 'true') {
        plugAndChargeValue = true;
      } else if (deviceDetails.plugAndCharge === 'false') {
        plugAndChargeValue = false;
      }

      const partnerCustomId =
        presetAccountCustomId || deviceDetails.accountCustomId || deviceDetails.accountName;

      const request: any = {
        ...(partnerCustomId ? { accountCustomId: partnerCustomId } : {}),
        chargingStation: deviceDetails.chargingStation,
        chargingPointName: deviceDetails.chargingPointName,
        chargingPointSn: deviceDetails.chargingPointSn,
        chargePointVendor: deviceDetails?.chargePointVendor,
        chargePointModel: deviceDetails?.chargePointModel,
        currentType: deviceDetails.currentType,
        skuNumber: deviceDetails.skuNumber,
        installationType: deviceDetails.installationType,
        networkConnectivity: deviceDetails.networkConnectivity,
        plugAndCharge: plugAndChargeValue,
        firmwareVersion: deviceDetails.firmwareVersion,
        maximumOutputPower: String(deviceDetails.maximumOutputPower || 0),
        maximumOutputPowerUnit: deviceDetails.maximumOutputPowerUnit || 'kW',
        protocolSupport: deviceDetails?.protocolSupport,
        chargeRate: {
          unit: deviceDetails.powerUnit,
          value: String(deviceDetails.desiredPowerOutput),
        },
        connectors: formattedConnectorInformation,
        imageUrl: deviceDetails.imageUrls,
        deviceAccessStatus: deviceDetails.status,
        // modelSeries: deviceDetails.modelSeries,
      };

      if (deviceDetails.powerUnit === 'W') {
        request.chargeRate.value = String(Number(deviceDetails.desiredPowerOutput) * 1000);
      }
      if (deviceDetails.maximumOutputPowerUnit === 'W') {
        request.maximumOutputPower = String(Number(deviceDetails.maximumOutputPower) * 1000);
      }
      deviceApi
        .addDevice(request)
        .then(async (response: any) => {
          dispatch(fetchAllDevices({ page, limit }));
          onSuccess?.();
          setFormStatus({
            showFormErrors: false,
            buttonLoader: false,
          });

          const deviceData =
            response?.data?.data ||
            response?.data?.chargingPoint ||
            response?.data?.result ||
            response?.data;

          let isTariffApplied = false;

          if (deviceData?.isTariffApplied !== undefined) {
            isTariffApplied = Boolean(deviceData.isTariffApplied);
          } else if (deviceData?.tariff || deviceData?.tariffId || deviceData?.tariffName) {
            isTariffApplied = true;
          } else if (Array.isArray(deviceData?.connectors) && deviceData.connectors.length > 0) {
            isTariffApplied = deviceData.connectors.some((connector: any) =>
              Boolean(connector?.tariffId || connector?.tariffName || connector?.tariff),
            );
          } else if (
            deviceData?.withTariffConnectors !== undefined &&
            Number(deviceData.withTariffConnectors) > 0
          ) {
            isTariffApplied = true;
          } else if (deviceData?._id || deviceData?.id) {
            try {
              const detailsResponse = await deviceApi.getChargingPointById(
                deviceData?._id || deviceData?.id,
              );
              const details = detailsResponse?.data?.data || detailsResponse?.data;
              if (details) {
                if (details.isTariffApplied !== undefined) {
                  isTariffApplied = Boolean(details.isTariffApplied);
                } else if (details.tariff || details.tariffId || details.tariffName) {
                  isTariffApplied = true;
                } else if (Array.isArray(details.connectors) && details.connectors.length > 0) {
                  isTariffApplied = details.connectors.some((connector: any) =>
                    Boolean(connector?.tariffId || connector?.tariffName || connector?.tariff),
                  );
                }
              }
            } catch {
              // Ignore lookup errors and fall back to isTariffApplied
            }
          }

          if (onDeviceAdded) {
            setSidePanelOpen(false);
            onDeviceAdded({ isTariffApplied });
            onSuccess?.();
            return;
          }

          setModalBoxStatus('open');
          if (!isTariffApplied) {
            setModalBoxDetail({
              title: null,
              content: (
                <TariffNotAppliedWarning
                  title={t('tariffNotApplied', 'Tariff Not Applied')}
                  description={t(
                    'tariffNotAppliedDescription',
                    'This charger has been added successfully, but no tariff has been applied. Please configure a tariff to enable charging.',
                  )}
                  cancelButtonLabel={t('cancel', 'Cancel')}
                  cancelButtonOnClick={() => {
                    setModalBoxDetail({
                      title: null,
                      content: (
                        <SuccessNotification
                          title={t('chargerAttachedSuccessfully')}
                          description={t('deviceAddedSuccessDescription')}
                          notificationCloseTimer={3000}
                          onNotificationClose={handleNavigateBack}
                        />
                      ),
                      notificationModal: true,
                    });
                  }}
                  submitButtonLabel={t('addTariff', 'Add Tariff')}
                  submitButtonOnClick={() => {
                    handleNavigateBack();
                    navigate('/ev-admin/tariffs/add-tariff');
                  }}
                />
              ),
              notificationModal: true,
            });
          } else {
            setModalBoxDetail({
              title: null,
              content: (
                <SuccessNotification
                  title={t('chargerAttachedSuccessfully')}
                  description={t('deviceAddedSuccessDescription')}
                  notificationCloseTimer={3000}
                  onNotificationClose={handleNavigateBack}
                />
              ),
              notificationModal: true,
            });
          }
        })
        .catch((error) => {
          setFormStatus({
            showFormErrors: true,
            buttonLoader: false,
          });
          dispatch(
            setNotificationMessage({
              message: error?.response?.data?.message || t('somethingWentWrong'),
              status: true,
              type: 'error',
              code: error?.response?.data?.code,
            }),
          );
        });
    } else {
      setFormStatus({
        showFormErrors: true,
        buttonLoader: false,
      });
      if (isAccountRequired && !isAccountValid) {
        dispatch(
          setNotificationMessage({
            message: t('accountNameRequired'),
            status: true,
            type: 'error',
          }),
        );
      } else if (!isStationValid) {
        dispatch(
          setNotificationMessage({
            message: t('partnerDeviceManagement:locationNameRequired'),
            status: true,
            type: 'error',
          }),
        );
      } else if (!isDeviceNameValid) {
        dispatch(
          setNotificationMessage({
            message: t('deviceNameRequired'),
            status: true,
            type: 'error',
          }),
        );
      } else if (!isDeviceIdEntered) {
        dispatch(
          setNotificationMessage({
            message: t('deviceIdRequired'),
            status: true,
            type: 'error',
          }),
        );
      } else if (deviceIdValidation.error) {
        dispatch(
          setNotificationMessage({
            message: deviceIdValidation.message || t('deviceIdNotFound'),
            status: true,
            type: 'error',
          }),
        );
      } else if (!isDesiredPowerValid) {
        const errorMessage =
          Number(deviceDetails.maximumOutputPower) > 0 &&
          Number(deviceDetails.desiredPowerOutput) > Number(deviceDetails.maximumOutputPower)
            ? `${t('desiredPowerCannotExceedMaxPower', 'Desired power cannot exceed maximum power limit')} (${deviceDetails.maximumOutputPower})`
            : t('desiredPowerOutputError');
        dispatch(
          setNotificationMessage({
            message: errorMessage,
            status: true,
            type: 'error',
          }),
        );
      }
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  /**
   * Debounce typing
   */
  useEffect(() => {
    setDeviceIdValidation({
      error: false,
      message: '',
    });

    const timer = setTimeout(() => {
      setDebounceChargingPointSn(deviceDetails.chargingPointSn);
    }, 1000);

    return () => clearTimeout(timer);
  }, [deviceDetails.chargingPointSn]);

  /**
   * API call after typing completed
   */
  useEffect(() => {
    if (debounceChargingPointSn?.trim().length > 1) {
      handleChargeList(debounceChargingPointSn);
    }
  }, [debounceChargingPointSn]);

  /**
   * station list Api
   */
  const handleStationNameList = async () => {
    if (!deviceDetails.accountCustomId) return;
    try {
      const response = await deviceApi.fetchChargingStationList(deviceDetails.accountCustomId);
      const mappedStations = (response.data || []).map((station: any) => ({
        label: station.name || '',
        value: station._id || station.id || '',
        description: station?.address,
      }));
      setAllStationsList(mappedStations);
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          message: error?.response?.data?.message || t('somethingWentWrong'),
          status: true,
          type: 'error',
          code: error?.response?.data?.code,
        }),
      );
    }
  };

  /**
   * Fetch account name list API and set the account name list state with the response data.
   */
  const handleAccountNameList = async () => {
    try {
      const response = await deviceApi.fetchAccountNameList();
      const mappedAccounts = response.data.map((account: AddDeviceProperties) => ({
        label: account?.accountName,
        value: account?.accountCustomId,
      }));
      setAccountNameList(mappedAccounts);

      if (portalType === PortalType.PARTNER && mappedAccounts.length > 0) {
        setDeviceDetails((previousDetails) => ({
          ...previousDetails,
          accountName: mappedAccounts[0].value,
          accountCustomId: mappedAccounts[0].value,
        }));
      }
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          message: error?.response?.data?.message || t('somethingWentWrong'),
          status: true,
          type: 'error',
          code: error?.response?.data?.code,
        }),
      );
    }
  };

  /**
   * Handling information icon click
   * @param {any} option - receiving options as a generic
   * @returns {any} - returning options values
   */
  const handleOpenFacilityInformation = (option: Option) => {
    navigate(`?accountCustomId=${option.value}`);
    setShowFacilityPanel(true);
  };

  /**
   * Handling information icon click
   * @param {any} option - receiving options as a generic
   * @returns {any} - returning options values
   */
  const handleOpenStationInformation = (option: Option) => {
    navigate(`?accountCustomId=${option.value}`);
    setShowFacilityPanel(true);
  };
  /**
   * handle Open Add Partner
   */
  const handleOpenAddPartner = () => {
    setModalBoxDetail({
      title: '',
      content: (
        <AddFacilityAccount
          closeModal={(message) => {
            setModalBoxStatus('close');
            if (message) {
              handleAccountNameList();
              dispatch(
                setNotificationMessage({
                  message,
                  status: true,
                  type: 'success',
                }),
              );
            }
          }}
          sidebarClosed={() => setModalBoxStatus('close')}
        />
      ),
      notificationModal: false,
    });
    setModalBoxStatus('open');
  };

  useEffect(() => {
    handleAccountNameList();
  }, []);

  /**
   * Pre-fill and lock the account/partner field when the panel was opened
   * from a specific partner's details page.
   */
  useEffect(() => {
    if (presetAccountCustomId) {
      setDeviceDetails((previousDetails) => ({
        ...previousDetails,
        accountName: presetAccountCustomId,
        accountCustomId: presetAccountCustomId,
      }));
    }
  }, [presetAccountCustomId]);

  useEffect(() => {
    if (deviceDetails?.accountName) {
      setDeviceDetails((previousDetails) => ({
        ...previousDetails,
        accountCustomId:
          accountNameList.find((account) => account.value === deviceDetails.accountName)?.value ||
          previousDetails.accountCustomId ||
          deviceDetails.accountName,
      }));
    }
  }, [deviceDetails?.accountName, accountNameList]);

  useEffect(() => {
    if (deviceDetails?.chargingStation) {
      const selectedStation = allStationsList.find(
        (station) => station.value === deviceDetails.chargingStation,
      );

      setDeviceDetails((previousDetails) => ({
        ...previousDetails,
        stationLocation: selectedStation?.description,
      }));
    }
  }, [deviceDetails?.chargingStation, allStationsList]);

  useEffect(() => {
    if (!deviceDetails.accountCustomId) return;
    handleStationNameList();
  }, [deviceDetails.accountCustomId]);

  if (showFacilityPanel) {
    return (
      <FacilityDetailsSidePanel
        actionActive
        isEditMode={isEditMode}
        onEditModeChange={setIsEditMode}
        fetchIdFrom="queryParams"
        onBack={() => {
          setShowFacilityPanel(false);
          setIsEditMode(false);
        }}
      />
    );
  }

  return (
    <div className="add-device-panel-partner">
      <ModalBox
        open={modalBoxStatus === 'open'}
        title={modalBoxDetail.title}
        content={modalBoxDetail.content}
        closeModal={() => setModalBoxStatus('close')}
        modalBoxAnimation="modalAnimateToLeft"
        notification={modalBoxDetail.notificationModal}
        header={!modalBoxDetail.notificationModal}
      />
      {!hideHeader && (
        <div className="panel-header">
          <h2>{t('addDevice')}</h2>
        </div>
      )}
      <div className="panel-form">
        <div className="form-grid">
          <div className="full-width-field">
            <h4>{t('basicInfo')}</h4>
          </div>
          {(PortalType.NETWORKS === portalType || PortalType.APP === portalType) && (
            <SelectFiled
              name="accountName"
              inputLabel={t('accountName')}
              placeholder={t('enterAccountName')}
              options={accountNameList}
              showInformationIcon
              onInformationClick={(options) => handleOpenFacilityInformation(options)}
              description={(option) => option.value}
              selectedValue={deviceDetails?.accountName ?? ''}
              onChangeValue={handleDeviceDetailChange}
              error={!deviceDetails.accountName && formStatus.showFormErrors}
              errorMessage={t('accountNameRequired')}
              valueRequired
              enableSearch
              emptyActionLabel={t('station:addAccount') || 'Add Partner'}
              onEmptyActionClick={handleOpenAddPartner}
              disabled={Boolean(presetAccountCustomId)}
              tooltipText={
                presetAccountCustomId
                  ? t('accountLockedTooltip', 'This field is locked to the selected partner.')
                  : undefined
              }
              conditionalToolTip={Boolean(presetAccountCustomId)}
            />
          )}

          <SelectFiled
            inputLabel={t('partnerDeviceManagement:locationName')}
            name="chargingStation"
            placeholder={t('partnerDeviceManagement:selectLocationName')}
            options={
              PortalType.NETWORKS === portalType || PortalType.APP === portalType
                ? allStationsList
                : partnerStations
            }
            showInformationIcon
            onInformationClick={(options) => handleOpenStationInformation(options)}
            description={(option) => option.description}
            selectedValue={deviceDetails.chargingStation}
            onChangeValue={handleDeviceDetailChange}
            error={!deviceDetails.chargingStation && formStatus.showFormErrors}
            errorMessage={t('partnerDeviceManagement:locationNameRequired')}
            valueRequired
            enableSearch
          />

          <CustomInput
            inputLabel={t('deviceName')}
            type="text"
            placeholder={t('enterDeviceName')}
            valueRequired
            name="chargingPointName"
            value={deviceDetails.chargingPointName}
            onChangeValue={handleDeviceDetailChange}
            error={!deviceDetails.chargingPointName && formStatus.showFormErrors}
            errorMessage={t('deviceNameRequired')}
          />
          <CustomInput
            inputLabel={t('deviceId')}
            type="text"
            placeholder={t('enterDeviceId')}
            name="chargingPointSn"
            valueRequired
            value={deviceDetails.chargingPointSn}
            onChangeValue={handleDeviceDetailChange}
            error={
              deviceIdValidation.error ||
              (!deviceDetails.chargingPointSn?.trim() && formStatus.showFormErrors)
            }
            errorMessage={deviceIdValidation.message || t('deviceIdRequired')}
            requiredTooltip={deviceIdValidation.error}
            tooltipText={t('deviceTooltipText')}
          />
          <CustomInput
            inputLabel={t('desiredPowerOutputKw', 'Desired Power Output (kW)')}
            type="number"
            placeholder={t('enterValue')}
            valueRequired
            name="desiredPowerOutput"
            value={deviceDetails.desiredPowerOutput}
            onChangeValue={(_, value) => {
              const inputValue = value.toString();

              const regex = /^\d{0,4}(\.\d{0,3})?$/;
              if (inputValue === '' || regex.test(inputValue)) {
                handleDeviceDetailChange('desiredPowerOutput', inputValue);
              }
            }}
            error={
              (!floatingNumberValidation(0.1, Infinity, Number(deviceDetails.desiredPowerOutput)) &&
                formStatus.showFormErrors) ||
              (Number(deviceDetails.maximumOutputPower) > 0 &&
                Number(deviceDetails.desiredPowerOutput) > Number(deviceDetails.maximumOutputPower))
            }
            errorMessage={
              Number(deviceDetails.maximumOutputPower) > 0 &&
              Number(deviceDetails.desiredPowerOutput) > Number(deviceDetails.maximumOutputPower)
                ? `${t('desiredPowerCannotExceedMaxPower', 'Desired power cannot exceed maximum power limit')} (${deviceDetails.maximumOutputPower})`
                : t('desiredPowerOutputError')
            }
            decimalLimit={3}
            maxValue={1000}
          />
        </div>
        <div className="form-grid">
          <div className="full-width-field">
            <h4>{t('chargerInfo')}</h4>
            <TooltipIcon text={t('chargerInfoTooltip')} />
          </div>
          <CustomInput
            inputLabel={t('maximumOutputPowerKw', 'Maximum Output Power (kW)')}
            type="number"
            placeholder={t('enterValue')}
            name="maximumOutputPower"
            value={deviceDetails.maximumOutputPower}
            onChangeValue={handleDeviceDetailChange}
            requiredTooltip={Boolean(deviceDetails.chargePointModel)}
            tooltipText={t(
              'autoPopulatedDeviceIdTooltip',
              'This field is automatically populated based on the Device ID and cannot be edited.',
            )}
            disabled
          />
          <CustomInput
            inputLabel={t('chargePointVendor')}
            type="text"
            placeholder={t('chargePointVendor')}
            name="chargePointVendor"
            value={deviceDetails.chargePointVendor}
            onChangeValue={handleDeviceDetailChange}
            requiredTooltip={Boolean(deviceDetails.chargePointVendor)}
            tooltipText={t(
              'autoPopulatedDeviceIdTooltip',
              'This field is automatically populated based on the Device ID and cannot be edited.',
            )}
            disabled
          />
          <CustomInput
            inputLabel={t('chargePointModel')}
            type="text"
            placeholder={t('chargePointModel')}
            name="chargePointModel"
            value={deviceDetails.chargePointModel}
            onChangeValue={handleDeviceDetailChange}
            requiredTooltip={Boolean(deviceDetails.chargePointModel)}
            tooltipText={t(
              'autoPopulatedDeviceIdTooltip',
              'This field is automatically populated based on the Device ID and cannot be edited.',
            )}
            disabled
          />
          <CustomInput
            inputLabel={t('skuNumber', 'SKU Number')}
            type="text"
            placeholder={t('skuNumber', 'SKU Number')}
            name="skuNumber"
            value={deviceDetails.skuNumber || ''}
            onChangeValue={handleDeviceDetailChange}
            tooltipText={t(
              'autoPopulatedDeviceIdTooltip',
              'This field is automatically populated based on the Device ID and cannot be edited.',
            )}
            disabled
          />
          <CustomInput
            inputLabel={t('installationType', 'Installation Type')}
            type="text"
            placeholder={t('installationType', 'Installation Type')}
            name="installationType"
            value={deviceDetails.installationType || ''}
            onChangeValue={handleDeviceDetailChange}
            tooltipText={t(
              'autoPopulatedDeviceIdTooltip',
              'This field is automatically populated based on the Device ID and cannot be edited.',
            )}
            disabled
          />
          <CustomInput
            inputLabel={t('networkConnectivity', 'Network Connectivity')}
            type="text"
            placeholder={t('networkConnectivity', 'Network Connectivity')}
            name="networkConnectivity"
            value={deviceDetails.networkConnectivity || ''}
            onChangeValue={handleDeviceDetailChange}
            tooltipText={t(
              'autoPopulatedDeviceIdTooltip',
              'This field is automatically populated based on the Device ID and cannot be edited.',
            )}
            disabled
          />
          <RadioButton
            inputLabel={t('plugAndCharge', 'Plug and Charge')}
            name="plugAndCharge"
            value={deviceDetails.plugAndCharge}
            onChangeValue={handleDeviceDetailChange}
            list={[
              { label: t('yes', 'Yes'), value: 'true' },
              { label: t('no', 'No'), value: 'false' },
            ]}
            isDisabled
          />
          <CustomInput
            inputLabel={t('currentType')}
            type="text"
            placeholder={t('currentType')}
            name="currentType"
            value={deviceDetails.currentType}
            onChangeValue={handleDeviceDetailChange}
            requiredTooltip={Boolean(deviceDetails.currentType)}
            tooltipText={t(
              'autoPopulatedDeviceIdTooltip',
              'This field is automatically populated based on the Device ID and cannot be edited.',
            )}
            disabled
          />
          <CustomInput
            inputLabel={t('firmwareVersion')}
            type="text"
            placeholder={t('firmwareVersion')}
            name="firmwareVersion"
            value={deviceDetails.firmwareVersion}
            onChangeValue={handleDeviceDetailChange}
            requiredTooltip={Boolean(deviceDetails.firmwareVersion)}
            tooltipText={t(
              'autoPopulatedDeviceIdTooltip',
              'This field is automatically populated based on the Device ID and cannot be edited.',
            )}
            disabled
          />
          {/* 
          <CustomInputSelectBox
            inputLabel={t('maximumPowerOutput')}
            inputType="number"
            placeholder={t('enterValue')}
            inputName="maximumOutputPower"
            selectName="maximumOutputPowerUnit"
            valueRequired
              inputValue={deviceDetails.maximumOutputPower}
            selectValue={deviceDetails.maximumOutputPowerUnit}
            selectOptions={maxPowerOutputUnits}
            handleValueChange={handleDeviceDetailChange}
            error={
              !floatingNumberValidation(1, Infinity, deviceDetails.maximumOutputPower) &&
              formStatus.showFormErrors
            }
            errorMessage={t('maxPowerOutputError')}
            disabled
          />

          <CustomInputSelectBox
            inputLabel={t('desiredPowerOutput')}
            inputType="number"
            placeholder={t('enterValue')}
            inputName="desiredPowerOutput"
            selectName="powerUnit"
            inputValue={deviceDetails.desiredPowerOutput}
            handleValueChange={handleDeviceDetailChange}
            error={
              !floatingNumberValidation(1, Infinity, deviceDetails.desiredPowerOutput) &&
              formStatus.showFormErrors
            }
            valueRequired
            errorMessage={t('desiredPowerOutputError')}
            selectValue={deviceDetails.powerUnit}
            selectOptions={localizedDesiredPowerOutputUnits}

          /> */}
        </div>
        <div className="connector-card-wrapper">
          {(deviceDetails.connectorInformation || []).map((connector, index) => (
            <div className="connector-card" key={connector._id || index}>
              <div className="connector-header">
                <h4>{`${t('connector', 'Connector')} ${index + 1}`}</h4>
              </div>

              <div className="connector-grid">
                <div className="connector-item">
                  <span className="label">{t('connectorType', 'Connector Type')}</span>
                  <span className="value">{connector.connectorType || '-'}</span>
                </div>

                <div className="connector-item">
                  <span className="label">{t('connectorFormat', 'Connector Format')}</span>
                  <span className="value">{connector.connectorFormat || '-'}</span>
                </div>

                <div className="connector-item">
                  <span className="label">{t('connectorRating', 'Connector Rating')}</span>
                  <span className="value">
                    <span className="value">
                      {connector.currentType || '-'}{' '}
                      {ToFixedValue(connector?.connectorRating?.capacityKW) || '-'}{' '}
                      {connector?.unit || '-'}
                    </span>
                  </span>
                </div>

                <div className="connector-item">
                  <span className="label">{t('maxCurrent', 'Max Current')}</span>
                  <span className="value">{connector.maxCurrent || '-'} A</span>
                </div>

                <div className="connector-item">
                  <span className="label">{t('maxVoltage', 'Max Voltage')}</span>
                  <span className="value">{connector.maxVoltage || '-'} V</span>
                </div>
                <div className="connector-item">
                  <span className="label">{t('maxPower', 'Max Power')}</span>
                  <span className="value">{connector.maxPower || '-'} kW</span>
                </div>
                <div className="connector-item">
                  <span className="label">{t('currentType', 'Current Type')}</span>
                  <span className="value">{connector.currentType || '-'}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="panel-footer">
        <MyButton label={t('cancel')} buttonType="back" onClickFunc={handleClickCancel} />
        <MyButton
          label={t('submit')}
          buttonType="submit"
          onClickFunc={handleSubmitDeviceDetails}
          disableButton={formStatus.buttonLoader}
          loading={formStatus.buttonLoader}
        />
      </div>
    </div>
  );
}

AddDevice.defaultProps = {
  hideHeader: false,
  presetAccountCustomId: undefined,
  onSuccess: undefined,
  onDeviceAdded: undefined,
};

export default AddDevice;
